import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Trash2,
  Plus,
  Pencil,
  X,
  Check,
  Languages,
  BookUser,
  Contact2,
  FileSignature,
  Stamp,
  Upload,
  ArrowRight,
  Search,
} from "lucide-react";
import { CIVIL_GLOSSARY_TYPE } from "./civilDocs";
import PageHeader from "./PageHeader";
import "./rules.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL !== undefined
    ? process.env.REACT_APP_API_BASE_URL
    : "http://localhost:5001";

const LANGUAGES = [
  { code: "ru", name: "Русский" },
  { code: "en", name: "Английский" },
  { code: "uz", name: "Узбекский" },
  { code: "tj", name: "Таджикский" },
  { code: "kz", name: "Казахский" },
  { code: "ky", name: "Кыргызский" },
  { code: "az", name: "Азербайджанский" },
  { code: "tk", name: "Туркменский" },
  { code: "hy", name: "Армянский" },
  { code: "ka", name: "Грузинский" },
  { code: "tr", name: "Турецкий" },
  { code: "de", name: "Немецкий" },
  { code: "fr", name: "Французский" },
  { code: "ar", name: "Арабский" },
  { code: "zh", name: "Китайский" },
  { code: "hi", name: "Хинди" },
  { code: "fa", name: "Персидский" },
  { code: "uk", name: "Украинский" },
  { code: "be", name: "Белорусский" },
  { code: "pl", name: "Польский" },
  { code: "bg", name: "Болгарский" },
  { code: "hr", name: "Хорватский" },
];

const TABS = [
  {
    key: "passport",
    label: "Паспорта",
    Icon: BookUser,
    tone: "lavender",
    description: "Замены при переводе паспортов (всегда на русский)",
  },
  {
    key: "drivers_license",
    label: "Водительские удостоверения",
    Icon: Contact2,
    tone: "mint",
    description: "Замены при переводе водительских удостоверений",
  },
  {
    key: "contract",
    label: "Документы / Договоры",
    Icon: FileSignature,
    tone: "peach",
    description: "Глоссарии для перевода договоров и документов",
  },
  {
    key: CIVIL_GLOSSARY_TYPE,
    label: "Документы РФ по шаблону",
    Icon: Stamp,
    tone: "butter",
    description:
      "Общий глоссарий для СОР, СОРБ, справок МВД, аттестатов и дипломов (органы ЗАГС, МВД, вузы)",
  },
];

const parseGlossaryText = (text) => {
  const lines = text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  const result = [];

  for (const line of lines) {
    let parts = null;

    if (line.includes("→")) {
      parts = line.split("→").map((p) => p.trim());
    } else if (line.includes("->")) {
      parts = line.split("->").map((p) => p.trim());
    } else if (line.includes("\t")) {
      parts = line.split("\t").map((p) => p.trim());
    } else if (line.includes(" — ")) {
      parts = line.split(" — ").map((p) => p.trim());
    } else if (line.includes(";")) {
      parts = line.split(";").map((p) => p.trim());
    }

    if (parts && parts.length === 2 && parts[0] && parts[1]) {
      result.push({ original_text: parts[0], translated_text: parts[1] });
    }
  }

  return result;
};

const RuleRow = ({
  rule,
  onEdit,
  onDelete,
  onToggle,
  editingId,
  editOriginal,
  editTranslated,
  setEditOriginal,
  setEditTranslated,
  onSaveEdit,
  onCancelEdit,
}) => {
  const isEditing = editingId === rule.id;

  // Enter — сохранить, Escape — отменить правку строки
  const onEditKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSaveEdit(rule.id);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancelEdit();
    }
  };

  return (
    <tr
      className={`${rule.is_active ? "" : "is-inactive"}${isEditing ? " is-editing" : ""}`}
    >
      <td className="rul-cell-status">
        <button
          type="button"
          role="switch"
          aria-checked={!!rule.is_active}
          className={`rul-switch${rule.is_active ? " is-on" : ""}`}
          onClick={() => onToggle(rule.id, rule.is_active)}
          aria-label={`Правило «${rule.original_text}» активно`}
          title={rule.is_active ? "Отключить" : "Включить"}
        >
          <span className="rul-switch-knob" aria-hidden="true" />
        </button>
      </td>
      <td className="rul-cell-orig">
        {isEditing ? (
          <input
            className="rul-input is-small"
            aria-label="Оригинал"
            value={editOriginal}
            onChange={(e) => setEditOriginal(e.target.value)}
            onKeyDown={onEditKeyDown}
            autoFocus
          />
        ) : (
          <code className="rul-term">{rule.original_text}</code>
        )}
      </td>
      <td className="rul-cell-trans">
        <ArrowRight
          size={16}
          className="rul-cell-arrow"
          aria-hidden="true"
        />
        {isEditing ? (
          <input
            className="rul-input is-small"
            aria-label="Перевод"
            value={editTranslated}
            onChange={(e) => setEditTranslated(e.target.value)}
            onKeyDown={onEditKeyDown}
          />
        ) : (
          <span className="rul-trans">{rule.translated_text}</span>
        )}
      </td>
      <td className="rul-cell-date">
        {new Date(rule.created_at).toLocaleDateString("ru-RU")}
      </td>
      <td className="rul-cell-actions">
        {isEditing ? (
          <div className="rul-actions">
            <button
              type="button"
              className="px-icon-btn is-small rul-save"
              onClick={() => onSaveEdit(rule.id)}
              aria-label="Сохранить правило"
              title="Сохранить"
            >
              <Check size={18} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="px-icon-btn is-small"
              onClick={onCancelEdit}
              aria-label="Отменить правку"
              title="Отменить"
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        ) : (
          <div className="rul-actions">
            <button
              type="button"
              className="px-icon-btn is-small rul-ghost"
              onClick={() => onEdit(rule)}
              aria-label={`Изменить правило «${rule.original_text}»`}
              title="Изменить"
            >
              <Pencil size={17} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="px-icon-btn is-small rul-ghost rul-delete"
              onClick={() => onDelete(rule.id)}
              aria-label={`Удалить правило «${rule.original_text}»`}
              title="Удалить"
            >
              <Trash2 size={17} aria-hidden="true" />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
};

const TranslationRulesPage = () => {
  const [activeTab, setActiveTab] = useState("passport");

  const [sourceLang, setSourceLang] = useState("ru");
  const [targetLang, setTargetLang] = useState("en");

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [originalText, setOriginalText] = useState("");
  const [translatedText, setTranslatedText] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [showBulkImport, setShowBulkImport] = useState(false);
  const [glossaryText, setGlossaryText] = useState("");
  const [parsedPreview, setParsedPreview] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [editOriginal, setEditOriginal] = useState("");
  const [editTranslated, setEditTranslated] = useState("");

  const [searchQuery, setSearchQuery] = useState("");

  const activeTabMeta = TABS.find((t) => t.key === activeTab);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { document_type: activeTab };
      if (activeTab === "contract") {
        params.source_language = sourceLang;
        params.target_language = targetLang;
      }
      const res = await axios.get(`${API_BASE_URL}/api/translation-rules`, {
        params,
      });
      setRules(res.data || []);
    } catch (e) {
      setError("Не удалось загрузить правила");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [activeTab, sourceLang, targetLang]);

  useEffect(() => {
    fetchRules();
    setSearchQuery("");
    setShowAddForm(false);
    setShowBulkImport(false);
    setEditingId(null);
  }, [fetchRules]);

  const handleAddRule = async (e) => {
    e.preventDefault();
    if (!originalText.trim() || !translatedText.trim()) {
      setSaveError("Заполните оба поля");
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        original_text: originalText.trim(),
        translated_text: translatedText.trim(),
        document_type: activeTab,
      };
      if (activeTab === "contract") {
        payload.source_language = sourceLang;
        payload.target_language = targetLang;
      }
      await axios.post(`${API_BASE_URL}/api/translation-rules`, payload);
      setOriginalText("");
      setTranslatedText("");
      setShowAddForm(false);
      fetchRules();
    } catch (e) {
      setSaveError(e.response?.data?.error || "Ошибка при сохранении");
    } finally {
      setSaving(false);
    }
  };

  const handleGlossaryTextChange = (text) => {
    setGlossaryText(text);
    setParsedPreview(parseGlossaryText(text));
    setBulkResult(null);
  };

  const handleBulkImport = async () => {
    if (parsedPreview.length === 0) return;
    setBulkSaving(true);
    setBulkResult(null);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/translation-rules/bulk`,
        {
          rules: parsedPreview,
          document_type: activeTab,
          source_language: sourceLang,
          target_language: targetLang,
        },
      );
      setBulkResult(res.data);
      setGlossaryText("");
      setParsedPreview([]);
      fetchRules();
    } catch (e) {
      setBulkResult({ error: e.response?.data?.error || "Ошибка импорта" });
    } finally {
      setBulkSaving(false);
    }
  };

  const handleEdit = (rule) => {
    setEditingId(rule.id);
    setEditOriginal(rule.original_text);
    setEditTranslated(rule.translated_text);
  };

  const handleSaveEdit = async (id) => {
    if (!editOriginal.trim() || !editTranslated.trim()) {
      alert("Заполните оба поля");
      return;
    }
    try {
      await axios.patch(`${API_BASE_URL}/api/translation-rules/${id}`, {
        original_text: editOriginal.trim(),
        translated_text: editTranslated.trim(),
      });
      setEditingId(null);
      fetchRules();
    } catch (e) {
      alert("Ошибка при сохранении");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Удалить это правило?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/translation-rules/${id}`);
      fetchRules();
    } catch (e) {
      alert("Ошибка удаления");
    }
  };

  const handleToggle = async (id, current) => {
    try {
      await axios.patch(`${API_BASE_URL}/api/translation-rules/${id}`, {
        is_active: !current,
      });
      fetchRules();
    } catch (e) {
      alert("Ошибка изменения статуса");
    }
  };

  const filteredRules = rules.filter((r) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      r.original_text.toLowerCase().includes(q) ||
      r.translated_text.toLowerCase().includes(q)
    );
  });

  const langName = (code) =>
    LANGUAGES.find((l) => l.code === code)?.name || code;

  const tabRefs = useRef({});

  // на узком экране лента вкладок прокручивается — держим активную в поле зрения
  useEffect(() => {
    tabRefs.current[activeTab]?.scrollIntoView?.({
      block: "nearest",
      inline: "nearest",
    });
  }, [activeTab]);

  // стрелки/Home/End переключают вкладки (паттерн WAI-ARIA tabs)
  const onTabKeyDown = (e) => {
    const idx = TABS.findIndex((t) => t.key === activeTab);
    let next = null;
    if (e.key === "ArrowRight") next = (idx + 1) % TABS.length;
    else if (e.key === "ArrowLeft") next = (idx - 1 + TABS.length) % TABS.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = TABS.length - 1;
    if (next === null) return;
    e.preventDefault();
    const key = TABS[next].key;
    setActiveTab(key);
    tabRefs.current[key]?.focus();
  };

  const cancelAddForm = () => {
    setShowAddForm(false);
    setOriginalText("");
    setTranslatedText("");
    setSaveError(null);
  };

  const closeBulkImport = () => {
    setShowBulkImport(false);
    setGlossaryText("");
    setParsedPreview([]);
    setBulkResult(null);
  };

  const isContract = activeTab === "contract";
  const tone = activeTabMeta?.tone || "lavender";
  const TabIcon = activeTabMeta?.Icon || Languages;

  return (
    <div className="rul-page">
      <PageHeader
        title="Словарь переводов"
        subtitle="Пары «оригинал → перевод» для автоматической замены при распознавании и переводе документов"
      >
        {!showAddForm && (
          <button
            type="button"
            className="px-btn px-btn-ink rul-add-btn"
            onClick={() => {
              setShowAddForm(true);
              setShowBulkImport(false);
            }}
          >
            <Plus size={20} strokeWidth={2.5} aria-hidden="true" />
            Добавить правило
          </button>
        )}
      </PageHeader>

      <div
        className="rul-tabs"
        role="tablist"
        aria-label="Тип документа"
        onKeyDown={onTabKeyDown}
      >
        {TABS.map((tab) => {
          const active = activeTab === tab.key;
          const { Icon } = tab;
          return (
            <button
              key={tab.key}
              ref={(el) => {
                tabRefs.current[tab.key] = el;
              }}
              type="button"
              role="tab"
              id={`rul-tab-${tab.key}`}
              aria-selected={active}
              aria-controls="rul-panel"
              tabIndex={active ? 0 : -1}
              className={`rul-tab${active ? " is-active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <span className={`rul-tab-icon tone-${tab.tone}`} aria-hidden="true">
                <Icon size={16} strokeWidth={2.2} />
              </span>
              {tab.label}
            </button>
          );
        })}
      </div>

      <div
        id="rul-panel"
        role="tabpanel"
        aria-labelledby={`rul-tab-${activeTab}`}
        className="rul-panel"
      >
        <section className={`rul-context tone-${tone}`} aria-label="О словаре">
          <div className="rul-context-main">
            <span className="rul-context-icon" aria-hidden="true">
              <TabIcon size={24} strokeWidth={2} />
            </span>
            <div className="rul-context-text">
              <p className={`rul-context-eyebrow is-${tone}`}>
                {activeTabMeta?.label}
              </p>
              <p className="rul-context-desc">{activeTabMeta?.description}</p>
            </div>
          </div>

          {isContract && (
            <div className="rul-context-tools">
              <div className="rul-langs">
                <label className="rul-lang">
                  <span className="rul-lang-label">Язык оригинала</span>
                  <select
                    className="rec-select rul-select"
                    value={sourceLang}
                    onChange={(e) => setSourceLang(e.target.value)}
                  >
                    {LANGUAGES.map((l) => (
                      <option key={l.code} value={l.code}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                </label>
                <ArrowRight
                  size={20}
                  className="rul-langs-arrow"
                  aria-hidden="true"
                />
                <label className="rul-lang">
                  <span className="rul-lang-label">Язык перевода</span>
                  <select
                    className="rec-select rul-select"
                    value={targetLang}
                    onChange={(e) => setTargetLang(e.target.value)}
                  >
                    {LANGUAGES.map((l) => (
                      <option key={l.code} value={l.code}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <button
                type="button"
                className="px-btn rul-btn-light"
                aria-expanded={showBulkImport}
                onClick={() => {
                  setShowBulkImport(!showBulkImport);
                  setShowAddForm(false);
                }}
              >
                <Upload size={18} aria-hidden="true" />
                Импорт глоссария
              </button>
            </div>
          )}
        </section>

        {isContract && showBulkImport && (
          <section className="res-card rul-card" aria-labelledby="rul-bulk-title">
            <div className="res-card-head rul-card-head">
              <div>
                <p className="res-eyebrow">Импорт глоссария</p>
                <h2 className="res-card-title" id="rul-bulk-title">
                  Глоссарий {langName(sourceLang)} → {langName(targetLang)}
                </h2>
              </div>
              <button
                type="button"
                className="px-icon-btn is-small rul-ghost"
                aria-label="Закрыть импорт"
                onClick={closeBulkImport}
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            <div className="rul-hint">
              Каждая строка — <code className="rul-code">оригинал → перевод</code>.
              Также поддерживаются разделители <code className="rul-code">-&gt;</code>,{" "}
              <code className="rul-code">;</code>, табуляция и{" "}
              <code className="rul-code">{" — "}</code>
            </div>

            <label className="rec-field rul-field-full">
              <span className="rec-label">Текст глоссария</span>
              <textarea
                className="rul-textarea"
                value={glossaryText}
                onChange={(e) => handleGlossaryTextChange(e.target.value)}
                placeholder={
                  "SRC → Государственный центр регистрации\nDOB → Дата рождения\npassport → паспорт"
                }
              />
            </label>

            {parsedPreview.length > 0 && (
              <div className="rul-preview">
                <p className="rul-preview-title">
                  <Check size={18} strokeWidth={2.5} aria-hidden="true" />
                  Распознано {parsedPreview.length} правил
                </p>
                <ul className="rul-preview-list">
                  {parsedPreview.slice(0, 20).map((item, i) => (
                    <li key={i} className="rul-preview-row">
                      <code className="rul-term">{item.original_text}</code>
                      <ArrowRight
                        size={16}
                        className="rul-preview-arrow"
                        aria-label="переводится как"
                      />
                      <span className="rul-trans">{item.translated_text}</span>
                    </li>
                  ))}
                  {parsedPreview.length > 20 && (
                    <li className="rul-preview-more">
                      …и ещё {parsedPreview.length - 20}
                    </li>
                  )}
                </ul>
              </div>
            )}

            {bulkResult && (
              <div
                className={`rul-note ${bulkResult.error ? "is-error" : "is-ok"}`}
                role={bulkResult.error ? "alert" : "status"}
              >
                {bulkResult.error ? (
                  `Ошибка: ${bulkResult.error}`
                ) : (
                  <>
                    <Check size={18} strokeWidth={2.5} aria-hidden="true" />
                    {`Добавлено: ${bulkResult.created}, пропущено дубликатов: ${bulkResult.skipped}`}
                  </>
                )}
              </div>
            )}

            <div className="rul-form-actions">
              <button
                type="button"
                className="px-btn px-btn-ink"
                onClick={handleBulkImport}
                disabled={parsedPreview.length === 0 || bulkSaving}
              >
                {bulkSaving
                  ? "Импорт..."
                  : `Импортировать ${parsedPreview.length} правил`}
              </button>
              <button
                type="button"
                className="px-btn rul-btn-light"
                onClick={closeBulkImport}
              >
                Закрыть
              </button>
            </div>
          </section>
        )}

        {showAddForm && (
          <section className="res-card rul-card" aria-labelledby="rul-add-title">
            <div className="res-card-head rul-card-head">
              <div>
                <p className="res-eyebrow">Новое правило</p>
                <h2 className="res-card-title" id="rul-add-title">
                  {activeTabMeta?.label}
                  {isContract && (
                    <span className="rul-pair">
                      {langName(sourceLang)} → {langName(targetLang)}
                    </span>
                  )}
                </h2>
              </div>
            </div>
            <form
              onSubmit={handleAddRule}
              onKeyDown={(e) => {
                if (e.key === "Escape") cancelAddForm();
              }}
            >
              <div className="rul-form-grid">
                <label className="rec-field">
                  <span className="rec-label">
                    Оригинальный текст
                    {isContract && (
                      <span className="rul-label-lang">
                        {" "}
                        · {langName(sourceLang)}
                      </span>
                    )}
                  </span>
                  <input
                    className="rul-input"
                    value={originalText}
                    onChange={(e) => setOriginalText(e.target.value)}
                    placeholder="Текст на оригинальном языке"
                    autoFocus
                  />
                </label>
                <ArrowRight
                  size={22}
                  className="rul-form-arrow"
                  aria-hidden="true"
                />
                <label className="rec-field">
                  <span className="rec-label">
                    Перевод
                    {isContract && (
                      <span className="rul-label-lang">
                        {" "}
                        · {langName(targetLang)}
                      </span>
                    )}
                  </span>
                  <input
                    className="rul-input"
                    value={translatedText}
                    onChange={(e) => setTranslatedText(e.target.value)}
                    placeholder="Переведённый текст"
                  />
                </label>
              </div>

              {saveError && (
                <div className="rul-note is-error" role="alert">
                  {saveError}
                </div>
              )}

              <div className="rul-form-actions">
                <button
                  type="submit"
                  className="px-btn px-btn-ink"
                  disabled={saving}
                >
                  {saving ? "Сохранение..." : "Сохранить"}
                </button>
                <button
                  type="button"
                  className="px-btn rul-btn-light"
                  onClick={cancelAddForm}
                >
                  Отмена
                </button>
              </div>
            </form>
          </section>
        )}

        <div className="rul-list-head">
          <h2 className="px-section-title rul-list-title">
            Правила
            <span className="rul-count">
              {filteredRules.length}
            </span>
          </h2>
          <label className="hist-search rul-search">
            <Search size={18} aria-hidden="true" />
            <input
              type="search"
              aria-label="Поиск по правилам"
              placeholder="Поиск по оригиналу или переводу"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </label>
        </div>

        {loading ? (
          <div className="px-empty rul-empty" role="status">
            <p className="rul-empty-hint">Загрузка...</p>
          </div>
        ) : error ? (
          <div className="px-empty rul-empty" role="alert">
            <p className="rul-empty-error">{error}</p>
          </div>
        ) : filteredRules.length === 0 ? (
          <div className="px-empty rul-empty">
            <span className={`rul-empty-icon tone-${tone}`} aria-hidden="true">
              <Languages size={28} />
            </span>
            <p className="rul-empty-title">Правил не найдено</p>
            <p className="rul-empty-hint">
              {searchQuery
                ? "Попробуйте изменить поиск"
                : isContract
                  ? `Добавьте правила для пары ${langName(sourceLang)} → ${langName(targetLang)}`
                  : "Добавьте первое правило перевода"}
            </p>
          </div>
        ) : (
          <div className="px-table-wrap rul-table-wrap">
            <table className="rul-table">
              <thead>
                <tr>
                  <th scope="col" className="rul-cell-status">
                    Активно
                  </th>
                  <th scope="col">
                    Оригинал
                    {isContract && (
                      <span className="rul-th-lang"> · {langName(sourceLang)}</span>
                    )}
                  </th>
                  <th scope="col">
                    Перевод
                    {isContract && (
                      <span className="rul-th-lang"> · {langName(targetLang)}</span>
                    )}
                  </th>
                  <th scope="col">Дата</th>
                  <th scope="col" className="rul-cell-actions">
                    <span className="sr-only">Действия</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredRules.map((rule) => (
                  <RuleRow
                    key={rule.id}
                    rule={rule}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggle={handleToggle}
                    editingId={editingId}
                    editOriginal={editOriginal}
                    editTranslated={editTranslated}
                    setEditOriginal={setEditOriginal}
                    setEditTranslated={setEditTranslated}
                    onSaveEdit={handleSaveEdit}
                    onCancelEdit={() => setEditingId(null)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TranslationRulesPage;
